import React, { useState } from "react";
import {
  Chip,
  Collapse,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";

import { Meeting, Organisation, PatientSummary, User } from "../../../types";
import { getMeetingStatus } from "../../../types-helpers";
import {
  getCalendarMeetingTypeColor,
  getCalendarMeetingTypeLabel,
} from "../../../components/calendar/calendar-status-utils";
import { getAttendeeStatus } from "../../../components/attendees/utils";
import MeetingTimeLog from "./meeting-timelog";
import PatientChip from "../../../components/chips/patient-chip";
import UserChip from "../../../components/chips/user-chip";
import AttendeeChip from "../../../components/chips/attendee-chip";

interface AdminMeetingsTableRowProps {
  meeting: Meeting;
  userDictionary: Record<string, User>;
  teamDictionary: Record<string, Organisation>;
  patientDictionary: Record<string, PatientSummary>;
  selectedClientId: string;
}

const AdminMeetingsTableRow: React.FC<AdminMeetingsTableRowProps> = ({
  meeting,
  userDictionary,
  teamDictionary,
  patientDictionary,
  selectedClientId,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const { status: meetingStatus } = getMeetingStatus(meeting);

  // TODO: Since we aren't getting all of the patients at any point the patientDictionary doesnt have all of the patients in it... Again would be good to get patient summarys per meeting from server on load

  const getUserName = (userId: string) => {
    const user = userDictionary[userId];

    if (!user) {
      return "Unknown User";
    }

    return user.fullName || user.email;
  };

  const formatDate = (dateTime: string | Date) => {
    const date = new Date(dateTime);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTime = (dateTime: string | Date) => {
    const date = new Date(dateTime);

    // Round to nearest minute
    const seconds = date.getSeconds();
    if (seconds >= 30) {
      date.setMinutes(date.getMinutes() + 1);
    }

    // Convert to 12-hour format
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const hoursStr = hours.toString();

    return `${hoursStr}:${minutes} ${ampm}`;
  };

  if (!meeting) {
    return null;
  }

  return (
    <>
      <TableRow
        sx={{
          cursor: "pointer",
          "&:hover": {
            background:
              "linear-gradient(135deg, rgba(5, 84, 128, 0.08) 0%, rgba(0, 150, 136, 0.08) 100%)",
          },
          ...(isExpanded && {
            "& > td": {
              borderBottom: "none",
            },
          }),
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <TableCell align="center" sx={{ py: 1 }}>
          <Chip
            label={getCalendarMeetingTypeLabel(meetingStatus).toUpperCase()}
            size="small"
            sx={{
              height: "22px",
              borderRadius: 1.5,
              backgroundColor: getCalendarMeetingTypeColor(meetingStatus),
              color: "#FFFFFF",
              fontSize: "0.7rem",
              fontWeight: 700,
              border: "1px solid transparent",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />
        </TableCell>

        <TableCell sx={{ py: 1 }}>
          <Stack direction="column" spacing={0.5} alignItems="center">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {formatDate(meeting.startTime)}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {formatTime(meeting.startTime)}
              </Typography>
            </Stack>
          </Stack>
        </TableCell>

        <TableCell sx={{ py: 1 }}>
          <Stack direction="column" spacing={0.5} alignItems="center">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {meeting.endTime ? formatDate(meeting.endTime) : "-"}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {meeting.endTime ? formatTime(meeting.endTime) : "-"}
              </Typography>
            </Stack>
          </Stack>
        </TableCell>

        <TableCell sx={{ py: 1 }}>
          <UserChip name={getUserName(meeting.creatorId)} />
        </TableCell>

        <TableCell sx={{ py: 1 }}>
          {meeting.patientId ? (
            <PatientChip
              label={
                patientDictionary[meeting.patientId]?.displayId ||
                meeting.patientId
              }
              size="small"
            />
          ) : (
            <Typography variant="body2" color="text.disabled">
              No Patient ID
            </Typography>
          )}
        </TableCell>

        <TableCell sx={{ py: 1 }}>
          <Stack direction="row" flexWrap="wrap" sx={{ gap: "6px" }}>
            {meeting.attendeeIds.length > 0 &&
              meeting.attendeeIds.map((attendeeId) => {
                const attendee = userDictionary[attendeeId];

                if (!attendee) {
                  return null;
                }

                const attendeeTeams = (attendee.organisationIds || [])
                  .map(
                    (teamId) => teamDictionary[teamId],
                  )
                  .filter(
                    (team): team is Organisation =>
                      !!team,
                  )
                  .map((team) => ({
                    teamId: team.organisationId,
                    name: team.name,
                    color: team.color,
                  }));

                const attendeeStatus = getAttendeeStatus(
                  meetingStatus,
                  meeting.joinedInfo?.find(
                    (joinInfo) => joinInfo.userId === attendeeId,
                  ),
                );

                return (
                  <AttendeeChip
                    key={`attendee-${attendee.userId}`}
                    attendee={{
                      type: "user",
                      userId: attendee.userId,
                      label:
                        attendee.fullName || attendee.email || "Unknown user",
                      teams: attendeeTeams,
                      status: attendeeStatus,
                    }}
                    editable={false}
                  />
                );
              })}
            {meeting.attendeeIds.length === 0 && (
              <Typography variant="body2" color="text.disabled">
                No Attendees
              </Typography>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          sx={{
            py: 0,
            border: 0,
            px: 0,
            ...(isExpanded && {
              "&:last-child": {
                borderBottom: "1px solid rgba(224, 224, 224, 1)",
              },
            }),
          }}
          colSpan={6}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Stack
              sx={{
                backgroundColor: "#ebebeb",
                maxHeight: 300,
                overflow: "auto",
              }}
            >
              <MeetingTimeLog
                meeting={meeting}
                userDictionary={userDictionary}
                teamDictionary={teamDictionary}
                selectedClientId={selectedClientId}
              />
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default AdminMeetingsTableRow;
